const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/v1";

export const apiUrl = (path: string) => `${API_BASE}${path}`;

function installedPwa() {
  return window.matchMedia?.("(display-mode: standalone), (display-mode: fullscreen)").matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function requestHeaders(initial?: HeadersInit) {
  const headers = new Headers(initial);
  if (installedPwa()) headers.set("X-JA-Client-Mode", "standalone");
  return headers;
}

export function apiErrorMessage(status: number, body: unknown) {
  if (typeof body !== "object" || body === null) return `Não foi possível concluir (${status}).`;
  if ("message" in body && typeof body.message === "string" && body.message.trim()) return body.message;
  if ("errors" in body && Array.isArray(body.errors)) {
    const issue = body.errors.find((item): item is { message: string } => (
      typeof item === "object" && item !== null && "message" in item && typeof item.message === "string" && Boolean(item.message.trim())
    ));
    if (issue) return issue.message;
  }
  if ("title" in body && typeof body.title === "string" && body.title.trim()) return body.title;
  return `Não foi possível concluir (${status}).`;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: unknown) {
    super(apiErrorMessage(status, body));
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = requestHeaders(init.headers);
  if (init.body !== undefined && init.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  const body = response.status === 204 ? undefined : await response.json().catch(() => undefined);
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

export type ApiStreamEvent = { event: string; data: unknown };

export async function apiEventStream(
  path: string,
  init: RequestInit,
  onEvent: (event: ApiStreamEvent) => void,
): Promise<void> {
  const headers = requestHeaders(init.headers);
  headers.set("Accept", "text/event-stream");
  if (init.body !== undefined && init.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body);
  }
  if (!response.body) throw new Error("O navegador não conseguiu abrir a resposta em tempo real.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const consume = (block: string) => {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    }
    if (!data.length) return;
    const serialized = data.join("\n");
    let parsed: unknown = serialized;
    try { parsed = JSON.parse(serialized); } catch { /* eventos textuais continuam válidos */ }
    onEvent({ event, data: parsed });
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.search(/\r?\n\r?\n/);
    while (boundary >= 0) {
      const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? "\n\n";
      consume(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + separator.length);
      boundary = buffer.search(/\r?\n\r?\n/);
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) consume(buffer);
}

export async function apiBlob(path: string): Promise<Blob> {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: requestHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body);
  }
  return response.blob();
}

export const newIdempotencyKey = () => crypto.randomUUID();
